import React, { useState, useEffect } from "react";
import { Container, Row, Col, Button, Card } from "react-bootstrap";
import "bootstrap/dist/css/bootstrap.min.css";
import { FaYoutube } from "react-icons/fa";
import { useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import Swal from "sweetalert2";
import Navbar from "../../Pages/Navbar/AgencyNavbar";
import BaseUrl from '../../ApiServices/BaseUrl';

function Uploadvideo() {
  const navigate = useNavigate();
  const location = useLocation();
  const user = JSON.parse(localStorage.getItem("user")) || {};
  const userId = location.state?.userId || user.id || '';

  const [formData, setFormData] = useState({
    youtube_link: "",
    subscription: false,
    networking_opportunities: false
  });
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isSaved, setIsSaved] = useState(false);
  const [videoId, setVideoId] = useState("");

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    const fetchJobSeekerData = async () => {
      try {
        const response = await axios.get(`${BaseUrl}/api/job-seeker/${userId}`);
        if (response.data) {
          setFormData({
            youtube_link: response.data.upload_video || "",
            subscription: response.data.subscription || false,
            networking_opportunities: response.data.networking_opportunities || false
          });
          extractVideoId(response.data.upload_video);
          setIsSaved(true);
        }
      } catch (err) {
        if (err.response?.status !== 404) {
          console.error('Error fetching job seeker data:', err);
          setError(err.response?.data?.message || 'Failed to fetch video data');
        }
      }
    };

    if (userId) {
      fetchJobSeekerData();
    }
  }, [userId]);

  const extractVideoId = (url) => {
    if (!url) {
      setVideoId("");
      return;
    }

    // Regular YouTube URL patterns
    const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/;
    const match = url.match(regExp);

    if (match && match[2].length === 11) {
      setVideoId(match[2]);
    } else {
      setVideoId("");
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    extractVideoId(value);
    setIsSaved(false);
  };

  const saveData = async () => {
    setIsLoading(true);
    setError(null);

    Swal.fire({
      title: 'Saving...',
      allowOutsideClick: false,
      didOpen: () => {
        Swal.showLoading();
      }
    });

    try {
      const dataToSend = {
        user_id: userId,
        upload_video: formData.youtube_link,
        subscription: formData.subscription,
        networking_opportunities: formData.networking_opportunities
      };

      console.log('Sending data:', dataToSend);

      const response = await axios.post(`${BaseUrl}/api/job-seeker`, dataToSend);

      await Swal.fire({
        title: 'Success!',
        text: 'Your video information has been saved successfully.',
        icon: 'success',
        confirmButtonText: 'OK',
        confirmButtonColor: '#3085d6',
      });

      setIsSaved(true);
      return response.data;
    } catch (err) {
      console.error('Error saving data:', err);

      let errorMessage = err.response?.data?.message || 'Failed to save video information. Please try again.';

      if (err.response?.data?.error) {
        errorMessage += ` (Database Error: ${err.response.data.error})`;
      }

      await Swal.fire({
        title: 'Error!',
        text: errorMessage,
        icon: 'error',
        confirmButtonText: 'OK',
        confirmButtonColor: '#d33',
      });

      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  const handleCheckboxChange = (field) => {
    setFormData(prev => ({
      ...prev,
      [field]: !prev[field]
    }));
    setIsSaved(false);
  };

  const handleBack = () => {
    navigate('/a-resume', { state: { userId } });
  };

  const handleSave = async () => {
    try {
      await saveData();
    } catch (err) {
      // Error already handled in saveData
    }
  };

  const handleSubmit = async () => {
    try {
      await saveData();
      window.scrollTo(0, 0);
      navigate('/a-choosephoto', { state: { userId } });
    } catch (err) {
      // Error already handled in saveData
    }
  };

  return (
    <>
      <Navbar />
      <Container fluid className="py-4 px-3" style={{ background: "#f8f9fa", minHeight: "100vh", marginTop: "62px" }}>
        <Row className="justify-content-center">
          <Col xs={12} md={10} lg={8}>
            {/* Main Content Card */}
            <Card className="shadow-sm border-0" style={{ borderRadius: "0px" }}>
              <Card.Body className="p-4 p-md-5">
                {/* Header */}
                <h2
                  className="mb-4 fw-semibold text-center"
                  style={{
                    fontFamily: '"Segoe UI", "Roboto", sans-serif',
                    color: "#2c3e50",
                    fontSize: "2rem"
                  }}
                >
                  Upload Video (Optional)
                </h2>

                {/* YouTube Info Card */}
                <Card className="text-center mb-4 border-0" style={{
                  backgroundColor: "#e3ecff",
                  borderRadius: "0px",
                  border: "2px dashed #4d59d1"
                }}>
                  <Card.Body className="d-flex flex-column justify-content-center align-items-center p-4">
                    <FaYoutube size={70} color="#dc3545" className="mb-3" />
                    <p className="mb-0 fw-medium text-dark text-center" style={{ fontSize: '18px', lineHeight: '1.6' }}>
                      Uploading a YouTube video (<span className="text-primary fw-bold">Link</span>) showcasing your work experience
                      can significantly increase your chances of getting selected by employers quickly
                    </p>
                  </Card.Body>
                </Card>

                {error && (
                  <div className="alert alert-danger" role="alert">
                    {error}
                  </div>
                )}

                {/* YouTube Link Input */}
                <Card className="mb-4 border-0" style={{ backgroundColor: "#f8f9fa" }}>
                  <Card.Body className="p-4">
                    <label htmlFor="youtube_link" className="form-label fw-semibold" style={{ fontSize: '18px', color: "#2c3e50" }}>
                      Add YouTube intro video for faster selection
                    </label>
                    <input
                      type="text"
                      className="form-control border-0 shadow-sm"
                      id="youtube_link"
                      name="youtube_link"
                      value={formData.youtube_link}
                      onChange={handleInputChange}
                      placeholder="Enter YouTube video URL (e.g., https://www.youtube.com/watch?v=dQw4w9WgXcQ)"
                      style={{
                        height: '50px',
                        fontSize: '16px',
                        borderRadius: "0px",
                        padding: "12px 16px"
                      }}
                    />
                  </Card.Body>
                </Card>

                {/* Video Preview */}
                {videoId && (
                  <Card className="mb-4 border-0" style={{ backgroundColor: "#f8f9fa" }}>
                    <Card.Body className="p-4">
                      <h5 style={{ fontSize: '18px', marginBottom: '15px', color: "#2c3e50", fontWeight: "600" }}>
                        Video Preview:
                      </h5>
                      <div className="ratio ratio-16x9" style={{ borderRadius: "0px", overflow: "hidden" }}>
                        <iframe
                          src={`https://www.youtube.com/embed/${videoId}`}
                          title="YouTube video player"
                          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                          allowFullScreen
                          style={{ border: "none" }}
                        ></iframe>
                      </div>
                    </Card.Body>
                  </Card>
                )}

                {/* Subscription Options */}
                <Card className="border-0 mb-4" style={{ backgroundColor: "#f8f9fa" }}>
                  <Card.Body className="p-4">
                    {/* Subscribe Row 1 */}
                    <Row className="align-items-center mb-4">
                      <Col xs={12} md={3} className="mb-3 mb-md-0">
                        <Button
                          variant="primary"
                          className="w-100 shadow-sm"
                          style={{
                            backgroundColor: formData.subscription ? "#4d59d1" : "#a1bff3",
                            border: "none",
                            color: formData.subscription ? "white" : "#000",
                            height: "50px",
                            fontSize: "18px",
                            borderRadius: "0px",
                            fontWeight: "600",
                            transition: "all 0.3s ease"
                          }}
                          onClick={() => handleCheckboxChange('subscription')}
                          disabled={isLoading}
                        >
                          {formData.subscription ? 'Subscribed' : 'Subscribe'}
                        </Button>
                      </Col>
                      <Col xs={12} md={9}>
                        <p className="mb-0" style={{ fontSize: '18px', color: "#2c3e50", lineHeight: "1.5" }}>
                          Sign up for the newsletter?
                        </p>
                      </Col>
                    </Row>

                    {/* Subscribe Row 2 */}
                    <Row className="align-items-center">
                      <Col xs={12} md={3} className="mb-3 mb-md-0">
                        <Button
                          variant="primary"
                          className="w-100 shadow-sm"
                          style={{
                            backgroundColor: formData.networking_opportunities ? "#4d59d1" : "#a1bff3",
                            border: "none",
                            color: formData.networking_opportunities ? "white" : "#000",
                            height: "50px",
                            fontSize: "18px",
                            borderRadius: "0px",
                            fontWeight: "600",
                            transition: "all 0.3s ease"
                          }}
                          onClick={() => handleCheckboxChange('networking_opportunities')}
                          disabled={isLoading}
                        >
                          {formData.networking_opportunities ? 'Subscribed' : 'Subscribe'}
                        </Button>
                      </Col>
                      <Col xs={12} md={9}>
                        <p className="mb-0" style={{ fontSize: '18px', color: "#2c3e50", lineHeight: "1.5" }}>
                          Get more networking opportunities.
                        </p>
                      </Col>
                    </Row>
                  </Card.Body>
                </Card>

                {/* Navigation Buttons */}
                <Card className="border-0 mt-4" style={{ backgroundColor: "transparent" }}>
                  <Card.Body className="p-0">
                    <div className="d-flex justify-content-between gap-2" style={{ width: "100%" }}>
                      <button
                        className="nav-btn border-0 py-3"
                        style={{
                          flex: 1,
                          backgroundColor: "#6c757d",
                          color: "white",
                          fontSize: '18px',
                          borderRadius: "0px",
                          fontWeight: "600",
                          transition: "all 0.3s ease"
                        }}
                        onClick={handleBack}
                        disabled={isLoading}
                        onMouseEnter={(e) => e.target.style.backgroundColor = "#5a6268"}
                        onMouseLeave={(e) => e.target.style.backgroundColor = "#6c757d"}
                      >
                        Back
                      </button>
                      <button
                        className="nav-btn border-0 py-3"
                        style={{
                          flex: 1,
                          backgroundColor: "#28a745",
                          color: "white",
                          fontSize: '18px',
                          borderRadius: "0px",
                          fontWeight: "600",
                          transition: "all 0.3s ease"
                        }}
                        onClick={handleSubmit}
                        disabled={isLoading}
                        onMouseEnter={(e) => e.target.style.backgroundColor = "#218838"}
                        onMouseLeave={(e) => e.target.style.backgroundColor = "#28a745"}
                      >
                        Submit
                      </button>
                    </div>
                  </Card.Body>
                </Card>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>
    </>
  );
}

export default Uploadvideo;
